import type { StatusStyle } from "@/components/ui/status-badge";

export type MatchStatus =
  | "all_matched"
  | "partially_matched"
  | "unmatched"
  | "no_items";

export type ProductionEventType =
  | "LABEL_PRINTED" | "PICKED" | "IN_PRETREAT" | "CAUGHT_ACCEPT" | "CAUGHT_REDO"
  | "CATCH_REVERTED" | "BIN_ASSIGNED" | "BIN_RELEASED" | "PACKED" | "SHIPPED"
  | "INVENTORY_ERROR" | "PAP_DAMAGED" | "ORDER_BLOCKED" | "ORDER_RESUMED"
  | "TEST_BATCH_CREATED" | "TEST_DATA_DELETED" | "PICKING_DEFECTIVE" | "STATUS_OVERRIDE"
  | "READY_FOR_PICKUP" | "BIN_MOVED_TO_OOS" | "BIN_FORCE_RELEASED" | "ORDER_ROUTED"
  | "CANCELLED_RETURNED" | "MARKED_DEFECT" | "PRINT_WRONG_ITEM" | "PRINT_GARMENT_ISSUE"
  | "SHOPIFY_FULFILLMENT_ORDER_MOVED" | "FILM_CLAIMED_FROM_SHELF" | "FILM_DROP_OK"
  | "FILM_DROP_FAILED" | "PORTLESS_MANIFEST_RECOVERED";

// Sanctioned palette (DESIGN.md §1/§7). No `dark:` variants — the inventory
// app is light-mode only (DESIGN.md §16).
const EMERALD = "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"; // done / completed
const CYAN = "bg-cyan-100 text-cyan-800 hover:bg-cyan-100"; // in-motion / in-progress
const AMBER = "bg-amber-100 text-amber-800 hover:bg-amber-100"; // warning / attention
const RED = "bg-red-100 text-red-800 hover:bg-red-100"; // failed / error
const MUTED = "bg-muted text-muted-foreground hover:bg-muted"; // terminal / neutral / infra

/**
 * Production event → semantic colour bucket. Distinct event types collapse to a
 * shared colour on purpose: colour carries the *category* (done / in-progress /
 * attention / error / neutral); the label text carries the specific event.
 *
 * `READY_FOR_PICKUP` + `SHIPPED` are the only terminal-good outcomes (emerald);
 * `PACKED` is a progress milestone, not fulfilment-complete, so it reads as
 * in-motion (cyan) to keep emerald reserved for completed states.
 */
const PROD_EVENT_CLASS: Record<ProductionEventType, string> = {
  SHIPPED: EMERALD,
  READY_FOR_PICKUP: EMERALD,

  PACKED: CYAN,
  LABEL_PRINTED: CYAN,
  PICKED: CYAN,
  IN_PRETREAT: CYAN,
  CAUGHT_ACCEPT: CYAN,
  ORDER_RESUMED: CYAN,
  CATCH_REVERTED: CYAN,

  CAUGHT_REDO: AMBER,
  STATUS_OVERRIDE: AMBER,
  MARKED_DEFECT: AMBER,
  ORDER_BLOCKED: AMBER,
  PICKING_DEFECTIVE: AMBER,
  // A recovery implies an earlier silent failure got healed — attention-worthy.
  PORTLESS_MANIFEST_RECOVERED: AMBER,

  INVENTORY_ERROR: RED,
  PAP_DAMAGED: RED,
  PRINT_WRONG_ITEM: RED,
  PRINT_GARMENT_ISSUE: RED,
  CANCELLED_RETURNED: RED,
  FILM_DROP_FAILED: RED,

  FILM_CLAIMED_FROM_SHELF: CYAN,

  BIN_ASSIGNED: MUTED,
  BIN_RELEASED: MUTED,
  BIN_MOVED_TO_OOS: MUTED,
  BIN_FORCE_RELEASED: MUTED,
  ORDER_ROUTED: MUTED,
  TEST_BATCH_CREATED: MUTED,
  TEST_DATA_DELETED: MUTED,
  SHOPIFY_FULFILLMENT_ORDER_MOVED: MUTED,
  FILM_DROP_OK: MUTED,
};

const formatEventTypeLabel = (type: string) =>
  type
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");

/** Resolve a production event type into a {@link StatusStyle}. */
export function prodEventStatusStyle(type: ProductionEventType): StatusStyle {
  return {
    label: formatEventTypeLabel(type),
    className: PROD_EVENT_CLASS[type] ?? MUTED,
  };
}

/** Colour-only accessor for callers that already render their own label. */
export function prodEventClassName(type: ProductionEventType): string {
  return PROD_EVENT_CLASS[type] ?? MUTED;
}

const MATCH_STATUS_LABELS: Record<MatchStatus, string> = {
  all_matched: "All Matched",
  partially_matched: "Partially Matched",
  unmatched: "Unmatched",
  no_items: "No Items",
};

const MATCH_STATUS_CLASS: Record<MatchStatus, string> = {
  all_matched: EMERALD,
  partially_matched: AMBER,
  unmatched: RED,
  no_items: MUTED,
};

/** Resolve a match status into a {@link StatusStyle}. */
export function matchStatusStyle(status: MatchStatus): StatusStyle {
  return {
    label: MATCH_STATUS_LABELS[status] ?? status,
    className: MATCH_STATUS_CLASS[status] ?? MUTED,
  };
}

export type MovementType =
  | "sale"
  | "return"
  | "purchase"
  | "adjustment"
  | "damage"
  | "count"
  | "pre_print"
  // BLU-711: shared-SKU fallback consumption (owner pool decremented by a
  // client's order at a shared 3PL location).
  | "substitution"
  // BLU-811: order-stamped committed-quantity increment/release
  // (transaction_type='committed'/'uncommitted').
  | "committed"
  | "released"
  // BLU-1428: an order line's commitment moved between the client's own line
  // and the 3PL's back-up line (source='backup_commit'). Committed-space.
  | "backup_commit";

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  sale: "Sale",
  return: "Return",
  purchase: "Purchase",
  adjustment: "Adjustment",
  damage: "Damage",
  count: "Count",
  pre_print: "Pre-Print",
  substitution: "Substitution",
  committed: "Committed",
  released: "Released",
  // BLU-1527: the fallback only. Logs labels a back-up commit row by its
  // kind ("Substitute garment" or "3PL back-up", translated) and its filter
  // option covers both (backup-commit-notes.ts).
  backup_commit: "3PL back-up",
};

/**
 * Stock-movement type → semantic colour bucket (DESIGN.md §1/§7). The old
 * hand-rolled map used blue (sale), violet (purchase / pre_print) and slate
 * (adjustment) — none of which are sanctioned hues. They fold into the five
 * sanctioned buckets by meaning, NOT by inventing a colour:
 *  - Supply-side stock additions (return / purchase / pre_print) → emerald
 *    (positive). These are the "stock came in" category; the Qty column carries
 *    the exact sign, the badge carries the category.
 *  - Sale → cyan (the outbound, in-motion flow — the highest-frequency row, so
 *    it keeps a distinct, readable identity instead of collapsing to neutral).
 *  - Count → amber (a stocktake reconciliation needs attention).
 *  - Damage → red (a loss / write-off).
 *  - Adjustment → muted (a manual, neutral correction).
 *  - Committed / Released → muted (BLU-811: committed-space rows, not a
 *    physical stock movement — matches the BIN_ASSIGNED/BIN_RELEASED
 *    neutrality above; borrowing emerald/red here would imply stock moved).
 * brand-purple is reserved for chrome (single accent, DESIGN.md §1) and is
 * never used to categorise data, so the old violet does NOT become purple.
 */
const MOVEMENT_TYPE_CLASS: Record<MovementType, string> = {
  return: EMERALD,
  purchase: EMERALD,
  pre_print: EMERALD,
  sale: CYAN,
  count: AMBER,
  damage: RED,
  adjustment: MUTED,
  // Substitution is outbound order-driven consumption (the sale category);
  // the label carries the cross-org specificity, the colour the category.
  substitution: CYAN,
  committed: MUTED,
  released: MUTED,
  // BLU-1428: committed-space like Committed / Released.
  backup_commit: MUTED,
};

/** Resolve a stock-movement type into a {@link StatusStyle}. */
export function movementTypeStyle(type: MovementType): StatusStyle {
  return {
    label: MOVEMENT_TYPE_LABELS[type] ?? type,
    className: MOVEMENT_TYPE_CLASS[type] ?? MUTED,
  };
}
